import { State, Action, StateContext } from '@ngxs/store';
import { inject, Injectable } from '@angular/core';
import { catchError, tap } from 'rxjs';
import { NotificationService } from '../../app/services/notification.service';
import { ApiKeysStateModel } from './api-keys.model';
import { ApiKeysService } from './api-keys.service';
import {
  CreateApiKey,
  DeleteApiKey,
  GetAllApiKeys,
  GetApiScopes,
  UpdateApiKey,
} from './api-keys.actions';
import { PaginationParams } from '../general-store/general.model';

@State<ApiKeysStateModel>({
  name: 'apiKeys',
  defaults: {
    apiKeys: [],
    pagination: {
      totalItems: 0,
      totalPages: 0,
      currentPage: 0,
      itemsPerPage: 0,
    },
    headers: [],
    apiKeysLoading: false,
    apiKeysListLoading: false,
    currentParams: null,
    apiScopes: [],
    apiScopesLoading: false,
  },
})
@Injectable({ providedIn: 'root' })
export class ApiKeysState {
  private readonly notificationService = inject(NotificationService);
  private readonly apiKeysService = inject(ApiKeysService);

  private paramsMatch(
    params1: PaginationParams | null | undefined,
    params2: PaginationParams | null | undefined,
  ): boolean {
    if (!params1 && !params2) return true;
    if (!params1 || !params2) return false;
    return (
      params1.first === params2.first &&
      params1.rows === params2.rows &&
      params1.sortBy === params2.sortBy &&
      params1.sortOrder === params2.sortOrder &&
      params1.search === params2.search &&
      params1.filters === params2.filters
    );
  }

  @Action(GetAllApiKeys)
  getAllApiKeys(ctx: StateContext<ApiKeysStateModel>, action: GetAllApiKeys) {
    const state = ctx.getState();
    const requestedParams = action.payload || {
      first: 0,
      rows: 5,
      sortBy: 'createdAt',
      sortOrder: 'DESC',
      search: '',
      filters: '',
    };

    // Check if we already have data for these exact params
    if (
      this.paramsMatch(state.currentParams, requestedParams) &&
      state.apiKeys.length > 0 &&
      !state.apiKeysListLoading
    ) {
      // Data already loaded for these params, no need to fetch
      return;
    }

    ctx.patchState({ apiKeysListLoading: true });
    return this.apiKeysService.getAllApiKeys(requestedParams).pipe(
      tap((res) =>
        ctx.patchState({
          apiKeys: res.payload,
          pagination: res.pagination,
          headers: res.headers,
          apiKeysListLoading: false,
          currentParams: requestedParams,
        }),
      ),
      catchError(async (_error) => {
        ctx.patchState({ apiKeysListLoading: false });
      }),
    );
  }

  @Action(CreateApiKey)
  createApiKey(ctx: StateContext<ApiKeysStateModel>, action: CreateApiKey) {
    ctx.patchState({ apiKeysLoading: true });
    const state = ctx.getState();
    return this.apiKeysService.createApiKey(action.payload).pipe(
      tap((res) => {
        this.notificationService.openSuccessToast('API key created successfully!');

        // Patch state: add new API key and update pagination
        // Handle both array and single object responses
        const newApiKey = Array.isArray(res.payload) ? res.payload[0] : res.payload;
        if (newApiKey) {
          const updatedApiKeys = [newApiKey, ...state.apiKeys];
          const updatedPagination = {
            ...state.pagination,
            totalItems: state.pagination.totalItems + 1,
          };

          ctx.patchState({
            apiKeys: updatedApiKeys,
            pagination: updatedPagination,
            apiKeysLoading: false,
          });
        } else {
          ctx.patchState({ apiKeysLoading: false });
        }
      }),
      catchError(async (_error) => {
        ctx.patchState({ apiKeysLoading: false });
      }),
    );
  }

  @Action(DeleteApiKey)
  deleteApiKey(ctx: StateContext<ApiKeysStateModel>, action: DeleteApiKey) {
    ctx.patchState({ apiKeysLoading: true });
    const state = ctx.getState();
    return this.apiKeysService.deleteApiKey(action.id).pipe(
      tap(() => {
        this.notificationService.openSuccessToast('API key deleted successfully!');

        // Patch state: remove the API key from the array and update pagination
        const updatedApiKeys = state.apiKeys.filter((apiKey) => apiKey.id !== action.id);
        const updatedPagination = {
          ...state.pagination,
          totalItems: Math.max(0, state.pagination.totalItems - 1),
        };

        ctx.patchState({
          apiKeys: updatedApiKeys,
          pagination: updatedPagination,
          apiKeysLoading: false,
        });
      }),
      catchError(async (_error) => {
        ctx.patchState({ apiKeysLoading: false });
      }),
    );
  }

  @Action(UpdateApiKey)
  updateApiKey(ctx: StateContext<ApiKeysStateModel>, action: UpdateApiKey) {
    ctx.patchState({ apiKeysLoading: true });
    const state = ctx.getState();
    return this.apiKeysService.updateApiKey(action.id, action.payload).pipe(
      tap((res) => {
        this.notificationService.openSuccessToast('API key updated successfully!');

        // Patch state: update the API key in the array
        // Handle both array and single object responses
        const updatedApiKey = Array.isArray(res.payload) ? res.payload[0] : res.payload;
        if (updatedApiKey) {
          const updatedApiKeys = state.apiKeys.map((apiKey) =>
            apiKey.id === action.id ? updatedApiKey : apiKey,
          );

          ctx.patchState({
            apiKeys: updatedApiKeys,
            apiKeysLoading: false,
          });
        } else {
          ctx.patchState({ apiKeysLoading: false });
        }
      }),
      catchError(async (_error) => {
        ctx.patchState({ apiKeysLoading: false });
      }),
    );
  }

  @Action(GetApiScopes)
  getApiScopes(ctx: StateContext<ApiKeysStateModel>) {
    ctx.patchState({ apiScopesLoading: true });
    return this.apiKeysService.getApiScopes().pipe(
      tap((res) => {
        ctx.patchState({ apiScopes: res.payload, apiScopesLoading: false });
      }),
      catchError(async (_error) => {
        ctx.patchState({ apiScopesLoading: false });
      }),
    );
  }
}
